import React, { FC, useEffect, useState } from 'react';
import { Theme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import {
  getEmptyLoadRateServiceRow,
  SliceOrderRate,
  updateDriverPayConfirmationModal,
  updateShowRatesWarningLtl
} from './../../redux/features/orders/ordersSlice';
import {
  InvoiceService,
  InvoiceServiceStatus,
  InvoiceServiceUnit,
  getInvoiceServiceUnitLabel
} from './../../redux/api/invoiceService/invoiceService.model';
import { useGetInvoiceServicesQuery } from './../../redux/api/invoiceService/invoiceServiceApi';
import NumberUtils from './../../utils/numberUtils';
import CreateService from '../../pages/Invoices/ServicesTable/CreateService/CreateInvoiceService';
import { useFormikContext } from 'formik';
import useAuth from '../../providers/Auth/Auth.provider';
import { InvoiceSources } from '../../providers/Invoices/Invoices.model';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { useGetTenantAccountingSettingsQuery } from '../../redux/api/tenant/tenantApi';
import Header from './Header';
import TwAdvancedRateRow, { RowData } from './RateRow';
import { TwAutocompleteOption } from '../TwAutocomplete/TwAutocomplete';
import InvoiceUtils from '../../utils/invoiceUtils';
import useCheckDriverPay from '../../hooks/useCheckDriverPay';
import { EntityType } from '../../redux/Global.model';

interface TwAdvancedRatesProps {
  initialData?: SliceOrderRate[];
  showDescription?: boolean;
  totalLoadedMiles?: number;
  isImportLoad: boolean;
  onRatesUpdate?: (data: RowData[], resetDriverPayOverrides: boolean) => void;
  onRateRowDelete?: (id: string) => void;
  source?: InvoiceSources;

  /**
   * If true, the component will be disabled and used to display data only.
   */
  disabled?: boolean;

  /**
   * Form errors object from external form
   */
  formErrors?: Record<string, unknown>;

  /**
   * The division id to use for fetching invoice services
   * if not provided, the default division id will be used.
   */
  selectedDivisionId: string | null;

  /**
   * If true, the total loaded miles will be recalculated
   * this helps to avoid recalculating the total loaded miles
   * in components without locations data
   */
  recalculateTotalLoadedMiles?: boolean;

  /**
   * If true, the mileage calculator will be hidden
   */
  hideMileageCalculator?: boolean;

  /**
   * If true, hides QBO class
   * Default is false
   */
  hideQboClass?: boolean;
}

const TwAdvancedRates: FC<TwAdvancedRatesProps> = ({
  initialData,
  showDescription,
  totalLoadedMiles,
  recalculateTotalLoadedMiles = true,
  isImportLoad,
  onRatesUpdate,
  onRateRowDelete,
  source,
  disabled,
  formErrors,
  selectedDivisionId,
  hideMileageCalculator,
  hideQboClass = false
}) => {
  const {
    data: { userData }
  } = useAuth();
  const enableLtl = userData?.tenant?.ltl_enabled || false;
  const loadId = useAppSelector((state) => state.orders.order_id);
  const matchesMobileOrTablet =
    useMediaQuery((theme: Theme) => theme.breakpoints.down('md')) || isImportLoad;
  const anyDriverPayExists = useCheckDriverPay({ entity: EntityType.ORDER });
  const dispatch = useAppDispatch();

  const { data: invoiceServicesData } = useGetInvoiceServicesQuery(
    {
      queryParams: { source },
      filters: {
        status: loadId ? [InvoiceServiceStatus.ACTIVE, InvoiceServiceStatus.DISABLED] : undefined,
        divisionId: selectedDivisionId || ''
      }
    },
    {
      skip: !selectedDivisionId,
      selectFromResult: ({ data = { content: [], total: 0 } }) => ({
        data: {
          content: data.content.map((item, index) => ({
            id: item.id || index.toString(),
            label: getInvoiceServiceUnitLabel(item.name),
            value: item
          })),
          total: data.total
        }
      })
    }
  );

  const { data: divisionAccountingSettings, isLoading } = useGetTenantAccountingSettingsQuery({
    divisionId: selectedDivisionId || userData?.defaultDivision.id || ''
  });

  const quickbooksEnabled = !!divisionAccountingSettings?.division_qbo_credentials?.id;
  const isSalesTaxEnabled = !!divisionAccountingSettings?.sales_tax_enabled;

  const { errors, setFieldError } = useFormikContext<unknown>() ?? {};

  const [rowsData, setRowsData] = useState<RowData[]>([]);
  const [hasInitialData, setHasInitialData] = useState<boolean>(false);
  const [isFirstRender, setIsFirstRender] = useState<boolean>(true);
  const [isCreateServiceOpen, setIsCreateServiceOpen] = useState<boolean>(false);
  const [activeRow, setActiveRow] = useState<string | undefined>(undefined);
  const [total, setTotal] = useState<number>(0);
  const [defaultServiceLinehaulAmount, setDefaultServiceLinehaulAmount] = useState<
    number | undefined
  >(undefined);

  /**
   * This function returns the initial service option for a row
   * it checks if is invoice service or QBO service
   * this is used to find the service in services list by invoice_service_id or qbo_service_id
   *
   * @param param0
   * @returns
   */
  const getInitialServiceOption = ({
    serviceId,
    qboServiceId,
    orderRate
  }: {
    serviceId: string;
    qboServiceId?: string;
    orderRate: SliceOrderRate;
  }): TwAutocompleteOption => {
    // reconsider finding service by id
    // if we always have the service_name and unit stored in db
    // the problem finding service is sometimes services list is not loaded when this function is called
    // so we can't find the service by name and unit
    if (serviceId) {
      const service = invoiceServicesData.content.find((service) => service.value.id === serviceId);
      if (service) {
        return {
          id: service.value.id,
          label: getInvoiceServiceUnitLabel(service.value.name),
          value: service.value
        };
      }
    }

    if (qboServiceId) {
      const service = invoiceServicesData.content.find(
        (service) => service.value.id === qboServiceId
      );
      if (service) {
        return {
          id: service.value.id,
          label: getInvoiceServiceUnitLabel(service.value.name),
          value: service.value
        };
      }
    }

    return {
      id: qboServiceId || serviceId,
      label: orderRate.service_name || '',
      value: {
        id: qboServiceId || serviceId,
        name: orderRate.service_name,
        default_rate: 0,
        unit: orderRate.unit
      } as InvoiceService
    };
  };

   /**
   * This function calculates the total amount of all rows by:
   * 1. Calculating each row's amount with 4 decimal precision
   * 2. Rounding each row's amount down to 2 decimals before adding to total
   * 3. Returning final total with 2 decimal places
   *
   * We multiply by 10000 during row calculations to preserve 4 decimal places.
   * This is necessary because JavaScript floating point math can lose precision.
   *
   * Example for a single row:
   * For rate 0.0135 and quantity 47310:
   * 1. Calculate with 4 decimals:
   *    - 0.0135 * 47310 = 638.6850
   * 2. Round to 2 decimals before adding to total:
   *    - 638.6850 -> 638.68
   *
   * The final total will be the sum of all row amounts after
   * they have been individually rounded to 2 decimal places.
   *
   * @param rows - Array of RowData objects containing rate and quantity information
   * @returns number - total amount of all rows with 2 decimal precision
   */
  const calculateTotalAmount = (rows: RowData[]): number => {
    const totalInCents = rows.reduce((accInCents, row) => {
      let rowAmount: number;
      if (row.amount !== undefined) {
        rowAmount = row.amount || 0;
      } else {
        rowAmount = InvoiceUtils.calculateRowAmount({
          rate: row.rate || 0,
          quantity: row.quantity || 0,
          unit: row.unit as InvoiceServiceUnit,
          isQboService: row.qbo_service_id && row.qbo_service_id !== '' ? true : false
        });
      }
      // Round to 2 decimal places before adding to total
      const roundedAmount = Math.round(rowAmount * 100) / 100;
      return accInCents + Math.round(roundedAmount * 10000);
    }, 0);

    return totalInCents / 10000;
  };

  const handleAddRow = (): void => {
    const newRowsData = [
      ...rowsData,
      {
        ...getEmptyLoadRateServiceRow(),
        // NOTE: In pages/CreateInvoice/CreateInvoice.tsx:2130 there is a validation
        // that checks if the description is empty, if it is, the description
        // is set using invoiceDefaultDescriptionMaker
        // no description is generated when this component is used in the CreateInvoice page for now
        // due this is how it's working currently
        description: '',
        sort_order: rowsData.length + 1
      }
    ];
    setRowsData(newRowsData);
    onRatesUpdate && onRatesUpdate(newRowsData, true);
    const defaultServiceId = quickbooksEnabled
      ? divisionAccountingSettings?.default_qbo_invoice_service_id || ''
      : divisionAccountingSettings?.default_invoice_service_id || '';
    setDefaultServiceLinehaulAmount(
      InvoiceUtils.calculateDefaultServiceAmount({
        rows: newRowsData,
        defaultServiceId
      })
    );
  };

  const handleDataUpdate = (
    id: string,
    newData: RowData,
    resetDriverPayOverrides: boolean
  ): void => {
    const newRowsData = [...rowsData];
    const index = newRowsData.findIndex((row) => row.ui_id === id);
    newRowsData[index] = newData;
    setRowsData(newRowsData);
    const defaultServiceId = quickbooksEnabled
      ? divisionAccountingSettings?.default_qbo_invoice_service_id || ''
      : divisionAccountingSettings?.default_invoice_service_id || '';
    setDefaultServiceLinehaulAmount(
      InvoiceUtils.calculateDefaultServiceAmount({
        rows: newRowsData,
        defaultServiceId
      })
    );
    onRatesUpdate && onRatesUpdate(newRowsData, resetDriverPayOverrides);
  };

  const handleDeleteRow = (id: string): void => {
    const rowToDelete = rowsData.find((row) => row.ui_id === id);
    const newRowsData = rowsData.filter((row) => row.ui_id !== id);
    setRowsData(newRowsData);
    onRatesUpdate && onRatesUpdate(newRowsData, true);
    if (rowToDelete && onRateRowDelete && rowToDelete.id) {
      onRateRowDelete(rowToDelete.id);
    }
  };

  const handleOpenAddService = (rowId: string): void => {
    setActiveRow(rowId);
    setIsCreateServiceOpen(true);
  };

  const handleOnAddServiceSuccess = (newService: InvoiceService): void => {
    const newRowsData = [...rowsData];
    const calculatedQuantity = InvoiceUtils.getQuantityBasedOnUnitAndInvoiceSource({
      service: newService,
      totalLoadedMiles: totalLoadedMiles || 0,
      amountOfDefaultService: defaultServiceLinehaulAmount || 0,
      isQboEnabled: quickbooksEnabled || false
    });
    const index = newRowsData.findIndex((row) => row.ui_id === activeRow);
    newRowsData[index] = {
      ...newRowsData[index],
      qbo_service_id: undefined, // hard-coding as undefined because new only handle adding new TB service types
      qbo_service_type: undefined, // hard-coding as undefined because new only handle adding new TB service types
      invoice_service_id: newService.id,
      service_name: newService.name,
      unit: newService.unit,
      rate: newService.default_rate,
      quantity: calculatedQuantity || 0,
      initial_service_option: {
        id: newService.id,
        label: getInvoiceServiceUnitLabel(newService.name),
        value: newService
      },
      amount: InvoiceUtils.calculateRowAmount({
        rate: newService.default_rate || 0,
        quantity: calculatedQuantity || 0,
        unit: newService.unit as InvoiceServiceUnit,
        isQboService: false
      }),
      sort_order: newRowsData.length + 1
    };
    setRowsData(newRowsData);
    onRatesUpdate && onRatesUpdate(newRowsData, true);
    setActiveRow(undefined);
    setIsCreateServiceOpen(false);
  };

  const handleClearFormErrorById = (id: string): void => {
    // so I guess the CEDI page(s) are in a form and the create/edit invoice isn't so this doesn't work on invoice pages. I added this check to see if it exists before trying to call it but not sure if that's the best way to handle it and/or if it should be in a form - Lisa
    setFieldError && setFieldError(id, undefined);
  };

  useEffect(() => {
    if (isFirstRender) {
      setIsFirstRender(false);
    }
  }, []);

  useEffect(() => {
    // every time rowsData changes, calculate the total amount
    const totalAmount = calculateTotalAmount(rowsData);
    setTotal(totalAmount);
  }, [rowsData]);

  useEffect(() => {
    if (initialData) {
      const defaultServiceId = quickbooksEnabled
        ? divisionAccountingSettings?.default_qbo_invoice_service_id || ''
        : divisionAccountingSettings?.default_invoice_service_id || '';
      setDefaultServiceLinehaulAmount(
        InvoiceUtils.calculateDefaultServiceAmount({
          rows: initialData,
          defaultServiceId
        })
      );
      const data = initialData.map((row, index) => {
        const rowBody: RowData = {
          ...row,
          sort_order: row.sort_order || initialData.length + 1,
          invoice_service: row.invoice_service || undefined,
          amount: InvoiceUtils.calculateRowAmount({
            rate: row.rate || 0,
            quantity: row.quantity || 0,
            unit: row.unit as InvoiceServiceUnit,
            isQboService: row.qbo_service_id && row.qbo_service_id !== '' ? true : false
          }),
          initial_service_option: row.initial_service_option
            ? row.initial_service_option
            : getInitialServiceOption({
                serviceId: row.invoice_service_id || '',
                qboServiceId: row.qbo_service_id || '',
                orderRate: initialData[index]
              })
        };

        return rowBody;
      });
      setRowsData(data);
    } else {
      setRowsData([]);
    }
    setHasInitialData(!!initialData);
  }, [initialData]);

  useEffect(() => {
    if (hasInitialData && !isFirstRender) setHasInitialData(false);
  }, [totalLoadedMiles, isFirstRender]);

  return (
    <>
      <Grid container direction="column" rowSpacing={1} marginTop="16px">
        {!matchesMobileOrTablet && (
          <Header selectedDivisionId={selectedDivisionId || ''} hideQboClass={hideQboClass} />
        )}
        {[...rowsData]
          .sort(
            (r1: SliceOrderRate, r2: SliceOrderRate) =>
              (r1.sort_order || rowsData.length) - (r2.sort_order || rowsData.length)
          )
          .map((row, index) => (
            <TwAdvancedRateRow
              key={row.ui_id}
              id={row.ui_id}
              index={index}
              data={row}
              isFirstRender={isFirstRender}
              isMobile={matchesMobileOrTablet}
              services={invoiceServicesData.content.filter(
                (service) =>
                  service.value.status === InvoiceServiceStatus.ACTIVE ||
                  row.invoice_service_id === service.id
              )}
              hasInitialData={hasInitialData}
              setHasInitialData={setHasInitialData}
              onRateUpdate={handleDataUpdate}
              onAddServiceClick={handleOpenAddService}
              onDeleteIconClick={handleDeleteRow}
              showDescription={showDescription}
              totalLoadedMiles={totalLoadedMiles}
              recalculateTotalLoadedMiles={recalculateTotalLoadedMiles}
              allowDeleteAll={rowsData.length > 1}
              errors={errors || formErrors || {}}
              disabled={disabled}
              quickbooksEnabled={quickbooksEnabled}
              onClearFormErrorById={handleClearFormErrorById}
              defaultInvoiceServiceId={divisionAccountingSettings?.default_invoice_service_id}
              amountOfDefaultService={defaultServiceLinehaulAmount}
              isLoading={isLoading}
              divisionId={selectedDivisionId || ''}
              hideMileageCalculator={hideMileageCalculator}
              hideQboClass={hideQboClass}
            />
          ))}
      </Grid>
      <Grid container item marginTop="4px">
        <Grid item xs={matchesMobileOrTablet ? 6 : 8}>
          {disabled ? (
            <></>
          ) : (
            <Box
              sx={{
                cursor: 'pointer',
                color: '#3A4DE9',
                fontWeight: 500,
                fontSize: '14px',
                width: '80px'
              }}
              id="add-rate"
              onClick={(): void => {
                if (anyDriverPayExists) {
                  if (enableLtl) {
                    dispatch(
                      updateShowRatesWarningLtl({
                        open: true,
                        action: () => handleAddRow()
                      })
                    );
                  } else {
                    dispatch(
                      updateDriverPayConfirmationModal({
                        open: true,
                        action: () => handleAddRow()
                      })
                    );
                  }
                } else handleAddRow();
              }}
            >
              + Add rate
            </Box>
          )}
        </Grid>

        {matchesMobileOrTablet ? (
          <Grid
            item
            xs={6}
            display="flex"
            justifyContent="flex-end"
            paddingRight={matchesMobileOrTablet ? '24px' : undefined}
          >
            <Box
              component="label"
              sx={{
                fontFamily: 'Poppins',
                fontWeight: 600,
                lineHeight: '21px',
                letterSpacing: 0,
                fontSize: '14px',
                color: '#9CA3AF'
              }}
            >
              {isSalesTaxEnabled ? 'SUBTOTAL' : 'TOTAL'}
            </Box>
            <Box fontSize="14px" marginLeft="16px" id={`total-money`}>
              <Box component="span" color="#9CA3AF">
                ${' '}
              </Box>
              {NumberUtils.formatNumber(total, 2, 2)}
            </Box>
          </Grid>
        ) : (
          <>
            <Grid item xs={2} display="flex" justifyContent="flex-end">
              <Box
                component="label"
                sx={{
                  fontFamily: 'Poppins',
                  fontWeight: 600,
                  lineHeight: '21px',
                  letterSpacing: 0,
                  fontSize: '14px',
                  color: '#9CA3AF'
                }}
              >
                {isSalesTaxEnabled ? 'SUBTOTAL' : 'TOTAL'}
              </Box>
            </Grid>
            <Grid item xs={2} display="flex" alignItems="center">
              <Box fontSize="14px" marginLeft="8px" id={`total-money`}>
                <Box component="span" color="#9CA3AF">
                  ${' '}
                </Box>
                {total === 0 ? 0 : NumberUtils.getCurrency(total).replace('$', '')}
              </Box>
            </Grid>
          </>
        )}
      </Grid>

      <CreateService
        open={isCreateServiceOpen}
        onClose={(): void => setIsCreateServiceOpen(false)}
        onSuccess={handleOnAddServiceSuccess}
      />
    </>
  );
};

export default TwAdvancedRates;
